/**
 * # dxfBridge.newRecordHandles.context — the shared leaf of the imported-drawing export preparation
 *
 * The minting context and the small helpers BOTH `dxfBridge.newRecordHandles.ts`
 * (orchestrator: table records / blocks / entities) and
 * `dxfBridge.newRecordHandles.layouts.ts` (LAYOUT NEW) build on. Kept in a
 * dependency-free leaf so the orchestrator imports the layouts section and
 * never the reverse (no import cycle — `depcruise no-circular`).
 */
import {
  createHexHandleMinter,
  maxHexHandleValueBig,
  type DxfGroup,
} from "@dxf-ai-editor/dxf-io";

import type { HandleMinter } from "./dxfBridge.annotativeExport";
import { isHexHandle } from "./dxfBridge.conformantSkeleton.handles";
import { readTableHeadHandle } from "./dxfBridge.conformantSkeleton.tableOwners";
import { collectDocumentHandlesDeep } from "./dxfBridge.documentHandleCensus";
import type { BlockRecord, DrawingDocument } from "./model";

/** The HEADER `$HANDSEED` value of the document's passthrough HEADER, if any. */
export function documentHandSeed(
  document: Pick<DrawingDocument, "rawPassthroughSections">,
): string | undefined {
  for (const section of document.rawPassthroughSections ?? []) {
    if (section.name !== "HEADER") continue;
    const groups = section.groups as readonly DxfGroup[];
    for (let i = 0; i + 1 < groups.length; i += 1) {
      if (groups[i]!.code === 9 && groups[i]!.value.trim() === "$HANDSEED") {
        const seed = groups[i + 1]!.value.trim();
        return seed.length > 0 ? seed : undefined;
      }
    }
  }
  return undefined;
}

/** `true` when `handle` is absent / empty / not a hex handle. */
export function lacksOwnHandle(handle: string | undefined): boolean {
  return handle === undefined || !isHexHandle(handle.trim());
}

/**
 * The shared minting context of one preparation run: the `bigint` minter over
 * the document census plus the `claimed` own-handle set. Exposed to the
 * generic walker as a {@link HandleMinter} so a nested own handle (an
 * `attributes[].handle`, an ATTDEF `handle`) that is absent or already
 * claimed is reminted exactly like the skeleton does.
 */
export interface PreparationContext {
  /** Fresh handle: above the census maximum, never a census / claimed value. */
  mint(): string;
  /** Claim `handle` as an emitted own handle; `false` when already claimed. */
  claim(handle: string): boolean;
  /** Is `handle` (any case) already claimed? */
  isClaimed(handle: string): boolean;
  /** The walker-compatible minter (`has` / `reserve` on the claimed set). */
  walkerMinter: HandleMinter;
}

export function createPreparationContext(
  document: DrawingDocument,
  seed: string,
): PreparationContext {
  const census = new Set<string>();
  collectDocumentHandlesDeep(document, census);
  const seedValue = BigInt(`0x${seed}`);
  const censusMax = maxHexHandleValueBig(census);
  let floor = 0x100000n;
  if (censusMax > floor) floor = censusMax;
  if (seedValue - 1n > floor) floor = seedValue - 1n;
  const claimed = new Set<string>();
  const counter = createHexHandleMinter(
    floor,
    (lower) => census.has(lower) || claimed.has(lower),
  );
  const mint = (): string => {
    const handle = counter.mint();
    claimed.add(handle.toLowerCase());
    return handle;
  };
  const claim = (handle: string): boolean => {
    const lower = handle.trim().toLowerCase();
    if (lower.length === 0 || claimed.has(lower)) return false;
    claimed.add(lower);
    return true;
  };
  const isClaimed = (handle: string): boolean => claimed.has(handle.trim().toLowerCase());
  const walkerMinter = mint as HandleMinter;
  walkerMinter.has = isClaimed;
  walkerMinter.reserve = claim;
  return { mint, claim, isClaimed, walkerMinter };
}

/**
 * The `330` owner of a BLOCK_RECORD created after import: the captured
 * BLOCK_RECORD table head (`symbolTableHeaders.BLOCK_RECORD`), else the owner a
 * sibling record carries, else unset — never fabricated.
 */
export function blockRecordOwner(document: DrawingDocument): string | undefined {
  return (
    readTableHeadHandle(document.symbolTableHeaders?.BLOCK_RECORD) ??
    Object.values(document.blockRecords ?? {}).find(
      (r) => r.ownerHandle !== undefined && r.ownerHandle.length > 0,
    )?.ownerHandle
  );
}

/** `record` with a fresh own handle and (when missing) its table-head owner. */
export function mintBlockRecord(
  document: DrawingDocument,
  record: BlockRecord,
  ctx: PreparationContext,
): BlockRecord {
  const owner = blockRecordOwner(document);
  return {
    ...record,
    handle: ctx.mint(),
    ...(record.ownerHandle === undefined && owner !== undefined ? { ownerHandle: owner } : {}),
  };
}
